// import { DragIndicatorOutlined } from "@mui/icons-material"; // used by the removed drag handle
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import {
  Box,
  Chip,
  Grid,
  InputAdornment,
  Popper,
  PopperProps,
} from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { FormikErrors, FormikProps } from "formik";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AsyncSelectValue } from "../../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../../components/AsyncSelectScroll";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomSelectScroll from "../../../../../components/CustomSelectScroll";
import { FlexCenterWrapper } from "../../../../../components/DefaultNoDataWrapper";
import ErrorHelper from "../../../../../components/ErrorWrapper";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import NumberInput from "../../../../../components/NumberInput";
import MaskInput from "../../../../../components/MaskInput";
import OutlineCard from "../../../../../components/OutlineCard";
import Text from "../../../../../components/Text";
import DeleteIcon from "../../../../../components/app/DeleteIcon";
import {
  ADD_SUPER_BILL_PROCEDURE_INITIAL_STATE,
  PROCEDURE_CODE_DISCOUNT_TYPE,
} from "../../../../../constants/provider/super-bill/add-super-bill/add-super-bill";
import useDebouncedCallback from "../../../../../hooks/use-debounced-callback";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useGetAmountFromFeeScheduleByProvider } from "../../../../../queries/billings/use-get-amount-from-fee-schedule-by-type";
import {
  // GetSubTotalForPatientProcedureCodeProps,
  SubTotalProcedureCodePayload,
  useCheckSubTotalForPatientProcedureCode,
} from "../../../../../queries/billings/use-get-sub-total-for-patient-by-procedure-cd";
import useGetCptHcpcsCode from "../../../../../queries/catalog/use-get-cpt-hcp-medical-code";
import useGetModifiers from "../../../../../queries/modifiers/use-get-modifiers";
import {
  GetFeeScheduleAmountByTypeFeeScheduleTypeEnum,
  PatientProcedureCode,
  PatientProcedureCodeDiscountTypeEnum,
} from "../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  SECONDARY_TEXT_COLOR,
  getXtenantId,
  toTwoDecimals,
} from "../../../../../utils/utils";
import { NO_NUMBER_ARROWS_MUI } from "../../../../../utils/global-styles";
import CardView from "../../patient/patient-list/patient-chart-new/assessment/summary/components/CardView";
import DiagnosisPointers from "../common-components/DiagnosisPointers";
import {
  FeeScheduleAmount,
  ProcedureCodeFormRow,
  ProcedureCodeGridRow,
  ProcedureRowField,
} from "../billing-types";
import { Option } from "../../../../../constants/common";

interface ProcedureCodeGridProps {
  procedureCodeFormik: FormikProps<ProcedureCodeFormRow[]>;
  viewBill: boolean;
  procedureCodes?: PatientProcedureCode[];
  isFetching: boolean;
  isLoading: boolean;
  renderingProviderUuid?: string;
  total?: number;
  diagnosisCodesLength: number;
  isPatientResponsibilityInvoice?: boolean;
}

// Currency symbol shown inside the monetary inputs so the invoice form matches
// how amounts are rendered everywhere else in billing.
const dollarAdornment = {
  startAdornment: <InputAdornment position="start">$</InputAdornment>,
};

const ProcedureCodeGrid = ({
  // formiks,
  diagnosisCodesLength,
  procedureCodeFormik,
  viewBill = true,
  renderingProviderUuid,
  isPatientResponsibilityInvoice = false,
}: // procedureCodes,
ProcedureCodeGridProps) => {
  // A Patient Responsibility invoice presents the CPT rows the claim was
  // adjudicated on, so rows cannot be added or removed and the ERA breakdown
  // (Copay / Coinsurance / Deductible) is read-only.
  const canEditCodes = viewBill && !isPatientResponsibilityInvoice;
  const isAmountLocked = !viewBill || isPatientResponsibilityInvoice;
  const showSnackbar = useSnackbar();

  const location = useLocation();
  const isRoute = location.pathname;

  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: 100,
  });

  const [gridSorting] = useState<GridSortItem[]>([]);
  const [gridData, setGridData] = useState<ProcedureCodeGridRow[]>([]);
  const [searchKey, setSearchKey] = useState<string>("");
  const [input, setInput] = useState<string | null>("");

  const [modifierSearchKey, setModifierSearchKey] = useState<string>("");
  const [modifierPagination, setModifierPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: modifierData, isFetching: isFetchingModifiers } =
    useGetModifiers({
      searchString: modifierSearchKey,
      page: modifierPagination.page,
      size: modifierPagination.pageSize,
      xTENANTID: getXtenantId(),
    });

  // console.log("Grid data", gridData);
  // console.log("procedureCodeFormik", procedureCodeFormik.values);

  const { data, isFetching: codeFetching } = useGetCptHcpcsCode({
    gridSorting,
    pagination,
    searchKey,
    active: true,
    archive: false,
  });

  // Keep every catalog item seen across pages so a code picked after scrolling
  // can still be resolved to its full record when added to the grid.
  const codeByUuid = useRef<Record<string, any>>({});
  useEffect(() => {
    (data?.data?.content ?? []).forEach((it: any) => {
      if (it?.uuid) codeByUuid.current[it.uuid] = it;
    });
  }, [data?.data?.content]);

  // interface ParamCalculateSub extends GetSubTotalForPatientProcedureCodeProps {
  //   idx: number;
  // }
  // renderingProviderUuid, patientProcedureCode, xTENANTID,
  const { mutateAsync: checkSubTotal } =
    useCheckSubTotalForPatientProcedureCode();

  // Plain (non-debounced) sub-total calc for a single row. Kept separate from
  // the debounced wrapper so it can be invoked once per row when several codes
  // are auto-populated at the same time (a shared debounce timer would collapse
  // the concurrent calls into a single one).
  const calculateSubTotal = ({
    patientProcedureCode,
    idx,
  }: {
    patientProcedureCode: SubTotalProcedureCodePayload;
    idx: number;
  }) => {
    if (!patientProcedureCode) return;
    // When the charge is blank/zero the line total is definitively 0. Skip the
    // backend call (which returns the previously calculated amount) and reset
    // the total locally so it recalculates to 0.00. (ECRH-5380 - Issue 1)
    const charges = patientProcedureCode?.charges;
    const chargeAmount = Number(charges);
    if (
      charges === "" ||
      charges === null ||
      charges === undefined ||
      Number.isNaN(chargeAmount) ||
      chargeAmount === 0
    ) {
      procedureCodeFormik.setFieldValue(`[${idx}].total`, "0");
      return;
    }
    return checkSubTotal({
      patientProcedureCode: patientProcedureCode,
      renderingProviderUuid: renderingProviderUuid ?? undefined,
    }).then((data) => {
      procedureCodeFormik.setFieldValue(`[${idx}].total`, data.data.data);
    });
  };

  const debouncedCalculateSubTotal = useDebouncedCallback(
    calculateSubTotal,
    500
  );
  const { mutateAsync: getAmountFromFeeSchedule } =
    useGetAmountFromFeeScheduleByProvider();

  // Plain (non-debounced) fee schedule lookup + prefill for a single row index.
  // Debouncing uses one shared timer, so calling the debounced version in a loop
  // for N codes would clear all but the last timer and only prefill one row.
  // Calling this directly runs one independent lookup per code, each writing to
  // its own index.
  const applyFeeScheduleForCode = ({
    procedureCode,
    idx,
  }: {
    procedureCode: string;
    idx: number;
  }) => {
    if (!procedureCode || !renderingProviderUuid) return;
    return getAmountFromFeeSchedule({
      providerId: renderingProviderUuid,
      codeId: procedureCode,
      feeScheduleType:
        GetFeeScheduleAmountByTypeFeeScheduleTypeEnum.ProviderSpecific,
    }).then((data) => {
      const res = data?.data?.data as FeeScheduleAmount | undefined;

      const isModifierAvail = Object.keys(
        procedureCodeFormik?.values?.[idx]?.modifiers ?? {}
      )?.length;

      if (res?.modifier && !isModifierAvail) {
        procedureCodeFormik.setFieldValue(
          `[${idx}].modifiers`,
          Object.entries(res.modifier).map(([uuid, modDesc]) => {
            return { value: uuid, label: modDesc };
          })
        );
      }
      if (res?.amount) {
        procedureCodeFormik.setFieldValue(`[${idx}].charges`, res.amount);

        // recompute this row's sub-total so the auto-populated charge is
        // reflected in the invoice total (setFieldValue does not fire the input
        // onChange handlers). Uses the non-debounced calc so every row is
        // computed when several codes are prefilled together.
        const row = procedureCodeFormik.values?.[idx];
        calculateSubTotal({
          patientProcedureCode: {
            procedureCode: row?.procedureCode,
            quantity: row?.quantity ?? 1,
            charges: res.amount,
            discount: row?.discount,
            discountType: row?.discountType,
            patientProcedureCodeType: "SUPER_BILL",
          },
          idx,
        });
      }
      if (res?.fixed) {
        procedureCodeFormik.setFieldValue(`[${idx}].disableCharges`, true);
      }
    });
  };

  const debouncedGetAmountFromFeeSchedule = useDebouncedCallback(
    applyFeeScheduleForCode,
    0
  );

  // Track CPT codes whose fee schedule has already been looked up so prefilled
  // codes are only auto-populated once.
  const feeScheduleProcessedRef = useRef<Set<string>>(new Set());

  // Always point at the latest formik instance so async callbacks read/write
  // the freshest values (avoids stale-snapshot writes clobbering each other).
  const formikRef = useRef(procedureCodeFormik);
  formikRef.current = procedureCodeFormik;

  // Auto-populate quantity (default 1) and the provider-specific fee schedule
  // values (charges, modifiers, fixed) for CPT codes prefilled from the backend
  // (e.g. from the encounter). All pending codes are looked up in parallel and
  // applied in a SINGLE setValues so each row keeps its own data and no
  // resolving lookup overwrites another row's prefill. Manual additions are
  // handled in the CPT onChange (which marks the code as processed here).
  useEffect(() => {
    if (!renderingProviderUuid || !procedureCodeFormik.values?.length) return;
    // A PR invoice bills the patient responsibility from the posted ERA
    // (Copay + Coinsurance + Deductible), never the procedure charge. Looking up
    // the fee schedule here would prefill `charges` and overwrite each row's
    // total with the fee schedule amount.
    if (isPatientResponsibilityInvoice) return;

    const pending: { idx: number; codeUuid: string; hasCharges: boolean }[] =
      [];
    procedureCodeFormik.values.forEach((item, idx) => {
      const codeUuid = Object.keys(item?.procedureCode ?? {})[0];
      if (!codeUuid || feeScheduleProcessedRef.current.has(codeUuid)) return;
      feeScheduleProcessedRef.current.add(codeUuid);
      pending.push({ idx, codeUuid, hasCharges: !!item?.charges });
    });
    if (!pending.length) return;

    (async () => {
      // 1) fetch the provider-specific fee schedule for every pending code.
      // Rows that already have charges (editing an existing invoice) are kept.
      const feeResults = await Promise.all(
        pending.map(({ idx, codeUuid, hasCharges }) => {
          if (hasCharges)
            return Promise.resolve({
              idx,
              res: null as FeeScheduleAmount | null,
            });
          return getAmountFromFeeSchedule({
            providerId: renderingProviderUuid,
            codeId: codeUuid,
            feeScheduleType:
              GetFeeScheduleAmountByTypeFeeScheduleTypeEnum.ProviderSpecific,
          })
            .then((data) => ({
              idx,
              res: (data?.data?.data as FeeScheduleAmount | undefined) ?? null,
            }))
            .catch(() => ({ idx, res: null as FeeScheduleAmount | null }));
        })
      );

      // 2) build the next values from the latest committed state, defaulting
      // quantity and applying each fee schedule to its OWN index only.
      const next: ProcedureCodeFormRow[] = formikRef.current.values.map(
        (r) => ({ ...r })
      );
      pending.forEach(({ idx }) => {
        if (next[idx] && !next[idx].quantity) next[idx].quantity = 1;
      });
      feeResults.forEach(({ idx, res }) => {
        if (!res || !next[idx]) return;
        const isModifierAvail = Object.keys(next[idx].modifiers ?? {}).length;
        if (res.modifier && !isModifierAvail) {
          next[idx].modifiers = Object.entries(res.modifier).map(
            ([uuid, modDesc]) => ({ value: uuid, label: modDesc })
          );
        }
        if (res.amount) next[idx].charges = res.amount;
        if (res.fixed) next[idx].disableCharges = true;
      });

      // 3) compute the sub-total for every row that received a charge, again in
      // parallel, and merge the results into the same array.
      const totals = await Promise.all(
        feeResults
          .filter(
            (feeResult): feeResult is { idx: number; res: FeeScheduleAmount } =>
              !!feeResult.res?.amount
          )
          .map(({ idx, res }) => {
            const row = next[idx];
            return checkSubTotal({
              patientProcedureCode: {
                procedureCode: row?.procedureCode,
                quantity: row?.quantity ?? 1,
                charges: res.amount,
                discount: row?.discount,
                discountType: row?.discountType,
                patientProcedureCodeType: "SUPER_BILL",
              },
              renderingProviderUuid: renderingProviderUuid ?? undefined,
            })
              .then((data) => ({
                idx,
                total: data?.data?.data as number | undefined,
              }))
              .catch(() => ({ idx, total: undefined }));
          })
      );
      totals.forEach(({ idx, total }) => {
        if (total !== undefined && next[idx]) next[idx].total = total;
      });

      // 4) apply everything atomically so no index overwrites another.
      formikRef.current.setValues(next);
    })();
  }, [procedureCodeFormik.values, renderingProviderUuid]);
  const getKey = (k: ProcedureRowField, ind: number) => `${ind}.${k}`;

  const selectHandleChange = (
    k: ProcedureRowField,
    v: string | AsyncSelectValue | null,
    ind: number
  ) => {
    procedureCodeFormik.setFieldValue(getKey(k, ind), v);
  };

  /**
   * Formik types an array form's per-index error as `string | FormikErrors<T>`;
   * only the object form carries the per-field messages this grid renders.
   */
  const getRowError = (
    ind: number
  ): FormikErrors<ProcedureCodeFormRow> | undefined => {
    const rowError = procedureCodeFormik.errors?.[ind];
    return typeof rowError === "string" ? undefined : rowError;
  };

  // console.log("gridData", gridData);
  // console.log("procedure code formik ", procedureCodeFormik.values);

  // console.log("formiks", formiks.values);
  useEffect(() => {
    if (
      isRoute.split("/")[4] === "invoice" ||
      isRoute.split("/")[4] === "add-invoice" ||
      isRoute.split("/")[4] === "update-invoice" ||
      isRoute.split("/")[4] === "create-pr-invoice" ||
      (isRoute.split("/")[4] === "view-invoice" && gridData.length === 0)
    ) {
      // console.log("value check herer procuedure",procedureCodeFormik.values);

      setGridData(
        procedureCodeFormik.values.map((itm) => {
          return {
            uuid: itm?.uuid,
            codeVersion: "",
            type: itm?.type,
            code: Object.values(itm?.procedureCode ?? {})[0]?.split(" - ")[0],
            description: Object.values(itm?.procedureCode ?? {})[0]
              ?.split(" - ")
              ?.slice(1)
              ?.join(" - "),
            source: "",
            active: itm.active,
            archive: itm.archive,
            errorMessage: "",
            procedureCode: Object.keys(itm?.procedureCode ?? {})[0],
          };
        })
      );
    }
  }, [procedureCodeFormik.values]);

  // below flow is to view only data
  // useEffect(() => {
  //   if (procedureCodes) {
  //     console.log("Procedure Codes ", procedureCodes);
  //   }
  // }, [procedureCodes]);
  // useEffect(() => {
  //   console.log("gridData ->>", gridData);
  // }, [gridData]);

  return (
    <Box id={"Procedure Code"}>
      <CardView
        title="CPT Code"
        titleIcon={
          <ListAltOutlinedIcon sx={{ color: "orange" }} fontSize="small" />
        }
        endAction={
          canEditCodes && (
            // width={"638px"}
            <Box sx={{ paddingX: "5px" }} width={"30.5vw"}>
              <CustomSelectScroll
                key={`cpt-select-${gridData?.length ?? 0}`}
                loading={codeFetching}
                pagination={pagination}
                setPagination={setPagination}
                totalElements={data?.data?.page?.totalElements}
                onSearch={(_, val) => {
                  setSearchKey(val);
                  setPagination((p) => ({ ...p, page: 0 }));
                }}
                options={data?.data?.content?.reduce((acc: Option[], it) => {
                  if (
                    !gridData.some((item) => item.procedureCode === it.uuid)
                  ) {
                    acc.push({
                      label: `${it.code ? it.code : "--"} - ${it.description}`,
                      value: it.uuid,
                    });
                  }
                  return acc;
                }, [])}
                onChange={(v) => {
                  const curOption =
                    codeByUuid.current[v as string] ??
                    data?.data?.content?.find((it) => it.uuid === v);
                  setInput(v);
                  if (curOption) {
                    if (!gridData.some((item) => item.procedureCode === v)) {
                      setTimeout(() => setInput(""), 0);
                      setSearchKey("");
                      setPagination({ page: 0, pageSize: 100 });
                      setGridData((prevItems) => [
                        ...prevItems,
                        {
                          uuid: undefined,
                          codeVersion: "",
                          type: curOption.type,
                          code: curOption.code,
                          description: curOption.description,
                          source: "",
                          active: true,
                          archive: false,
                          errorMessage: "",
                          procedureCode: curOption.uuid,
                        },
                      ]);
                      // procedureCodeFormik.setValues((prev) => [
                      //   ...prev,
                      //   curOption,
                      // ]);
                      procedureCodeFormik.setValues([
                        ...(procedureCodeFormik.values ?? {}),
                        ADD_SUPER_BILL_PROCEDURE_INITIAL_STATE[0],
                      ]);
                      // console.log(
                      //   "value check here curOption",
                      //   curOption.type,
                      //   gridData,
                      // );
                      procedureCodeFormik.setFieldValue(
                        getKey(
                          "procedureCode",
                          procedureCodeFormik.values.length
                        ),
                        {
                          [curOption?.uuid]: `${curOption.code ?? "--"} - ${
                            curOption.description
                          }`,
                        }
                      );

                      procedureCodeFormik.setFieldValue(
                        `[${procedureCodeFormik.values.length}].type`,
                        curOption.type
                      );

                      // mark as processed so the prefill effect does not run a
                      // duplicate lookup for this manually added code
                      feeScheduleProcessedRef.current.add(curOption.uuid);

                      // to prefill the modifier data right after selection of the claim.
                      // Skipped for a PR invoice: the row is billed from the ERA
                      // breakdown, so it must not pick up a fee schedule charge.
                      if (!isPatientResponsibilityInvoice) {
                        setTimeout(() => {
                          debouncedGetAmountFromFeeSchedule({
                            procedureCode: curOption.uuid,
                            idx: procedureCodeFormik?.values?.length,
                          });
                        }, 0);
                      }
                    } else {
                      setSearchKey("");

                      setTimeout(() => setInput(""), 0);
                      showSnackbar({
                        message: "Procedure Code already exists",
                        type: "error",
                      });
                    }
                  }

                  // procedureCodeFormik.setFieldValue(
                  //   getKey("procedureCode", procedureCodeFormik.values.length),
                  //   curOption.uuid
                  // );
                }}
                value={input}
                placeholder="Search & select CPT Code"
              />
            </Box>
          )
        }
      >
        {/* <Text
            fontWeight={"bold"}
            variant="h6"
            fontSize={"14px"}
            color={SECONDARY_TEXT_COLOR}
            alignSelf={"left"}
            margin={"5px 0px"}
          >
            CPT Code
          </Text> */}

        {gridData.length === 0 && (
          <FlexCenterWrapper>
            <Text>No CPT Code Added</Text>
          </FlexCenterWrapper>
        )}
        {/* multiple box.  */}
        <Box overflow={"scroll"} maxHeight={"500px"} height={"100%"}>
          {gridData.map((itm, idx) => (
            <Flex
              key={itm.uuid}
              marginTop={"auto"}
              flexDirection={"column"}
              width={"100%"}
              gap={0.5}
            >
              <Flex flexDirection={"column"} height={"100%"} gap={1}>
                <OutlineCard
                  style={{
                    height: "30%",
                    paddingBlock: "15px",
                    width: "100%",
                    borderLeft: "30px solid #d8edf9",
                    position: "relative",
                    overflow: "visible",
                    margin: "10px 0px 10px 0px",
                  }}
                >
                  {/* The 6-dot handle never moved anything — rows are not
                      reorderable — so it is removed rather than left as a dead
                      affordance. Restore this block if drag-to-reorder is built:

                  <Box
                    sx={{
                      position: "absolute",
                      left: "-30px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: "#d8edf9",
                      padding: "1px",
                      border: "1px solid #d8edf9",
                      zIndex: 2,
                    }}
                  >
                    <DragIndicatorOutlined />
                  </Box> */}

                  {/* details of card  */}
                  <Flex
                    height={"30px"}
                    paddingLeft={"10px"}
                    justifyContent={"space-around"}
                    alignItems={"stretch"}
                  >
                    <Box width={"5%"}>
                      <Text
                        fontWeight={"bold"}
                        variant="h6"
                        fontSize={"14px"}
                        color={"primary.main"}
                        alignSelf={"center"}
                      >
                        {idx + 1}
                      </Text>
                    </Box>
                    <Box width={"5%"}>
                      <Text
                        fontWeight={"bold"}
                        variant="h6"
                        fontSize={"15px"}
                        color={SECONDARY_TEXT_COLOR}
                        alignSelf={"center"}
                      >
                        {itm?.code}
                      </Text>
                    </Box>
                    <Box width={"80%"}>
                      <Text
                        fontWeight={"bold"}
                        variant="h6"
                        fontSize={"15px"}
                        color={SECONDARY_TEXT_COLOR}
                        alignSelf={"center"}
                        marginLeft={"8px"}
                      >
                        {itm?.description}
                      </Text>
                    </Box>

                    <Box
                      sx={{
                        width: "10%",
                        display: "flex",
                        flexDirection: "row-reverse",
                      }}
                    >
                      {/* <ButtonBase
                        disabled={!viewBill}
                        onClick={() => {
                          setGridData(gridData.toSpliced(idx, 1));
                          procedureCodeFormik.setValues(
                            procedureCodeFormik.values.toSpliced(idx, 1)
                          );
                        }}
                      > */}
                      <DeleteIcon
                        disabled={!canEditCodes}
                        onClick={() => {
                          setGridData(gridData.toSpliced(idx, 1));
                          procedureCodeFormik.setValues(
                            procedureCodeFormik.values.toSpliced(idx, 1)
                          );
                        }}
                      />
                      {/* <Typography fontSize={"18px"} fontWeight={"500"}>
                          <DeleteOutlineIcon
                            color={"error"}
                            sx={{ cursor: "pointer" }}
                          />
                        </Typography>
                      </ButtonBase> */}
                      <Box
                        sx={{
                          borderRight: "1px solid lightgray",
                          paddingRight: "10px",
                          marginRight: "10px",
                        }}
                      >
                        <Chip
                          size="small"
                          color="primary"
                          // Falls back to "CPT" so the chip is never an empty
                          // box for a row whose code type was not sent.
                          label={
                            <Text fontSize={"12px"}>{itm?.type || "CPT"}</Text>
                          }
                          sx={{ borderRadius: "5px" }}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Flex>

                  {/* modifiers  diagnosis pointers and other details.
                      Wrapped in an overflow-x container with nowrap so the
                      fixed-width fields scroll as one unit instead of
                      overlapping when the viewport is narrow / zoomed.
                      (ECRH-5380) */}
                  <Box
                    sx={{ overflowX: "auto", width: "100%", paddingTop: "8px" }}
                  >
                    <Grid
                      container
                      paddingLeft={"10px"}
                      spacing={4}
                      flexWrap={"nowrap"}
                    >
                      {isPatientResponsibilityInvoice ? (
                        <Grid item xs={12}>
                          <Flex
                            flexDirection={"row"}
                            gap={"10px"}
                            alignItems={"end"}
                            height={"100%"}
                          >
                            <NumberInput
                              label="Copay"
                              textFieldProps={{
                                placeholder: "Copay",
                                disabled: isAmountLocked,
                                InputProps: dollarAdornment,
                                value: toTwoDecimals(
                                  procedureCodeFormik.values?.[idx]?.copay
                                ),
                                onChange: (
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = e.target.value;
                                  selectHandleChange("copay", value, idx);
                                  const coInsurance =
                                    parseFloat(
                                      String(
                                        procedureCodeFormik.values?.[idx]
                                          ?.coInsurance
                                      )
                                    ) || 0;
                                  const deductible =
                                    parseFloat(
                                      String(
                                        procedureCodeFormik.values?.[idx]
                                          ?.deductible
                                      )
                                    ) || 0;
                                  const total =
                                    (parseFloat(value) || 0) +
                                    coInsurance +
                                    deductible;
                                  procedureCodeFormik.setFieldValue(
                                    `[${idx}].total`,
                                    total.toFixed(2)
                                  );
                                },
                              }}
                            />
                            <NumberInput
                              label="Coinsurance"
                              textFieldProps={{
                                placeholder: "Coinsurance",
                                disabled: isAmountLocked,
                                InputProps: dollarAdornment,
                                value: toTwoDecimals(
                                  procedureCodeFormik.values?.[idx]?.coInsurance
                                ),
                                onChange: (
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = e.target.value;
                                  selectHandleChange("coInsurance", value, idx);
                                  const copay =
                                    parseFloat(
                                      String(
                                        procedureCodeFormik.values?.[idx]?.copay
                                      )
                                    ) || 0;
                                  const deductible =
                                    parseFloat(
                                      String(
                                        procedureCodeFormik.values?.[idx]
                                          ?.deductible
                                      )
                                    ) || 0;
                                  const total =
                                    copay +
                                    (parseFloat(value) || 0) +
                                    deductible;
                                  procedureCodeFormik.setFieldValue(
                                    `[${idx}].total`,
                                    total.toFixed(2)
                                  );
                                },
                              }}
                            />
                            <NumberInput
                              label="Deductible"
                              textFieldProps={{
                                placeholder: "Deductible",
                                disabled: isAmountLocked,
                                InputProps: dollarAdornment,
                                value: toTwoDecimals(
                                  procedureCodeFormik.values?.[idx]?.deductible
                                ),
                                onChange: (
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = e.target.value;
                                  selectHandleChange("deductible", value, idx);
                                  const copay =
                                    parseFloat(
                                      String(
                                        procedureCodeFormik.values?.[idx]?.copay
                                      )
                                    ) || 0;
                                  const coInsurance =
                                    parseFloat(
                                      String(
                                        procedureCodeFormik.values?.[idx]
                                          ?.coInsurance
                                      )
                                    ) || 0;
                                  const total =
                                    copay +
                                    coInsurance +
                                    (parseFloat(value) || 0);
                                  procedureCodeFormik.setFieldValue(
                                    `[${idx}].total`,
                                    total.toFixed(2)
                                  );
                                },
                              }}
                            />
                            <NumberInput
                              label="Total"
                              textFieldProps={{
                                disabled: true,
                                placeholder: "Total",
                                // Shown with 2 decimals, matching the amount
                                // fields it is calculated from.
                                value: toTwoDecimals(
                                  procedureCodeFormik.values?.[idx]?.total
                                ),
                                InputProps: dollarAdornment,
                              }}
                            />
                          </Flex>
                        </Grid>
                      ) : (
                        <>
                          <Grid item xs={4} sx={{ minWidth: "340px" }}>
                            <ErrorHelper label="Modifiers">
                              <Flex gap={1}>
                                {Array.from({ length: 4 }).map((_, id) => (
                                  <Box key={id} width={"25%"}>
                                    <AsyncSelectScroll
                                      disabled={!viewBill}
                                      loading={isFetchingModifiers}
                                      pagination={modifierPagination}
                                      setPagination={setModifierPagination}
                                      totalElements={
                                        modifierData?.data?.page?.totalElements
                                      }
                                      onSearch={(val) => {
                                        setModifierSearchKey(val);
                                        setModifierPagination((p) => ({
                                          ...p,
                                          page: 0,
                                        }));
                                      }}
                                      options={modifierData?.data?.content?.map(
                                        (it) => ({
                                          value: it.uuid,
                                          label: `${it.code}-${it.description ?? ""}`,
                                        })
                                      )}
                                      value={
                                        procedureCodeFormik.values?.[idx]
                                          ?.modifiers?.[id]
                                      }
                                      onChange={(
                                        value: AsyncSelectValue | null
                                      ) => {
                                        selectHandleChange(
                                          `modifiers.[${id}]`,
                                          value,
                                          idx
                                        );
                                      }}
                                      CustomPopperComponent={(
                                        props: PopperProps
                                      ) => (
                                        <Popper
                                          {...props}
                                          style={{
                                            width: 500,
                                          }}
                                          placement="bottom-start"
                                        />
                                      )}
                                    />
                                  </Box>
                                ))}
                              </Flex>
                            </ErrorHelper>
                          </Grid>
                          {/* Diagnosis Pointer */}
                          <DiagnosisPointers
                            diagnosisCodesLength={diagnosisCodesLength}
                            viewBill={viewBill}
                            procedureCodeFormik={procedureCodeFormik}
                            selectHandleChange={selectHandleChange}
                            idx={idx}
                          />
                          {/* <Grid item xs={4} alignSelf={"end"}>
                    <Box display={"flex"} flexDirection={"row"} gap={"5px"}> */}

                          <Grid item xs={5} sx={{ minWidth: "580px" }}>
                            <Flex
                              flexDirection={"row"}
                              gap={"10px"}
                              alignItems={"end"}
                              height={"100%"}
                            >
                              <MaskInput
                                mask={"99999"}
                                label="Quantity"
                                placeholder="Quantity"
                                disabled={!viewBill}
                                value={
                                  procedureCodeFormik.values?.[idx]?.quantity
                                }
                                onChange={(e) => {
                                  selectHandleChange(
                                    "quantity",
                                    e.target.value,
                                    idx
                                  );
                                  debouncedGetAmountFromFeeSchedule({
                                    procedureCode: `${itm?.procedureCode}`,
                                    idx: idx,
                                  });
                                  debouncedCalculateSubTotal({
                                    patientProcedureCode: {
                                      procedureCode: {
                                        [`${itm?.procedureCode}`]: `${itm.description}`,
                                      },
                                      quantity: e.target.value,
                                      charges:
                                        procedureCodeFormik.values?.[idx]
                                          ?.charges,
                                      discount:
                                        procedureCodeFormik.values?.[idx]
                                          ?.discount,
                                      discountType:
                                        procedureCodeFormik.values?.[idx]
                                          ?.discountType,
                                      patientProcedureCodeType: "SUPER_BILL",
                                    },
                                    idx: idx,
                                  });
                                }}
                              />
                              <Input
                                label="Charges"
                                textFieldProps={{
                                  type: "number",
                                  placeholder: "Charges",
                                  disabled:
                                    !viewBill ||
                                    procedureCodeFormik.values?.[idx]
                                      ?.disableCharges,
                                  value:
                                    procedureCodeFormik.values?.[idx]?.charges,
                                  onChange: (
                                    e: ChangeEvent<HTMLInputElement>
                                  ) => {
                                    const value = e.target.value;
                                    if (
                                      value === "" ||
                                      /^\d*\.?\d{0,2}$/.test(value)
                                    ) {
                                      selectHandleChange("charges", value, idx);
                                      // Clearing the charge resets the total to 0
                                      // immediately. (ECRH-5380 - Issue 1)
                                      if (value === "" || Number(value) === 0) {
                                        selectHandleChange("total", "0", idx);
                                      }
                                      debouncedCalculateSubTotal({
                                        patientProcedureCode: {
                                          procedureCode: {
                                            [`${itm?.procedureCode}`]: `${itm.description}`,
                                          },
                                          quantity:
                                            procedureCodeFormik.values?.[idx]
                                              ?.quantity,
                                          charges: value,
                                          discount:
                                            procedureCodeFormik.values?.[idx]
                                              ?.discount,
                                          discountType:
                                            procedureCodeFormik.values?.[idx]
                                              ?.discountType,
                                          patientProcedureCodeType:
                                            "SUPER_BILL",
                                        },
                                        idx: idx,
                                      });
                                    }
                                  },
                                  onBlur: (
                                    e: React.FocusEvent<HTMLInputElement>
                                  ) => {
                                    let value = e.target.value;
                                    if (value) {
                                      value = parseFloat(value).toFixed(2);
                                      selectHandleChange("charges", value, idx);
                                    }
                                  },
                                  InputProps: dollarAdornment,
                                  inputProps: {
                                    style: {
                                      MozAppearance: "textfield",
                                    },
                                    inputMode: "numeric",
                                    pattern: "^\\d*\\.?$",
                                  },
                                  sx: {
                                    width: "120px",
                                    padding: "0px",
                                    fontSize: "0.875rem",
                                    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                      {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                      },
                                    "& input[type=number]": {
                                      MozAppearance: "textfield",
                                    },
                                  },
                                }}
                              />
                              <Box sx={{ width: "150px", flexShrink: 0 }}>
                                <CustomSelect
                                  name="type"
                                  handleBlur={procedureCodeFormik.handleBlur}
                                  label="Discount Type"
                                  options={PROCEDURE_CODE_DISCOUNT_TYPE}
                                  placeholder="Select"
                                  readOnly={!viewBill}
                                  value={
                                    procedureCodeFormik.values?.[idx]
                                      ?.discountType
                                  }
                                  onChange={(v) => {
                                    selectHandleChange("discountType", v, idx);
                                    // Clear the previously entered discount when the
                                    // Discount Type changes so the user enters a new
                                    // value for the new type. (ECRH-5380)
                                    selectHandleChange("discount", "", idx);

                                    debouncedCalculateSubTotal({
                                      patientProcedureCode: {
                                        procedureCode: {
                                          [`${itm?.procedureCode}`]: `${itm.description}`,
                                        },
                                        quantity:
                                          procedureCodeFormik.values?.[idx]
                                            ?.quantity,
                                        charges:
                                          procedureCodeFormik.values?.[idx]
                                            ?.charges,
                                        discount: undefined,
                                        discountType:
                                          v as PatientProcedureCodeDiscountTypeEnum,
                                        patientProcedureCodeType: "SUPER_BILL",
                                      },
                                      idx: idx,
                                    });
                                  }}
                                  // error={formik.touched.type && formik.errors.type}
                                />
                              </Box>
                              {procedureCodeFormik.values?.[idx]
                                ?.discountType && (
                                <Box
                                  sx={{
                                    position: "relative",
                                    width: "110px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <Input
                                    clearable={false}
                                    label={`Discount (${
                                      procedureCodeFormik.values?.[idx]
                                        ?.discountType === "PERCENTAGE"
                                        ? "%"
                                        : "$"
                                    })`}
                                    textFieldProps={{
                                      type: "number",
                                      disabled: !viewBill,
                                      placeholder: "Discount",
                                      value:
                                        procedureCodeFormik.values?.[idx]
                                          ?.discount,
                                      inputProps: { min: 0 },
                                      onKeyDown: (e) => {
                                        // Discount can't be negative. (ECRH-5380)
                                        if (e.key === "-" || e.key === "+") {
                                          e.preventDefault();
                                        }
                                      },
                                      onChange: (e) => {
                                        selectHandleChange(
                                          "discount",
                                          e.target.value,
                                          idx
                                        );
                                        debouncedCalculateSubTotal({
                                          patientProcedureCode: {
                                            procedureCode: {
                                              [`${itm?.procedureCode}`]: `${itm.description}`,
                                            },
                                            quantity:
                                              procedureCodeFormik.values?.[idx]
                                                ?.quantity,
                                            charges:
                                              procedureCodeFormik.values?.[idx]
                                                ?.charges,
                                            discount: e.target.value,
                                            discountType:
                                              procedureCodeFormik.values?.[idx]
                                                ?.discountType,
                                            patientProcedureCodeType:
                                              "SUPER_BILL",
                                          },
                                          idx: idx,
                                        });
                                      },
                                      sx: {
                                        width: "110px",
                                        flexShrink: 0,
                                        ...NO_NUMBER_ARROWS_MUI,
                                      },
                                    }}
                                    // Pass a boolean so the field only turns red; the
                                    // message is rendered absolutely below so a long
                                    // validation text can't grow this cell and push
                                    // the row out of alignment. (ECRH-5380 - Issue 7)
                                    error={!!getRowError(idx)?.discount}
                                  />
                                  {!!getRowError(idx)?.discount && (
                                    <Text
                                      color="error"
                                      sx={{
                                        position: "absolute",
                                        top: "100%",
                                        left: 0,
                                        width: "200px",
                                        fontSize: "10px",
                                        lineHeight: 1.2,
                                        mt: "2px",
                                      }}
                                    >
                                      {getRowError(idx)?.discount}
                                    </Text>
                                  )}
                                </Box>
                              )}
                              <Input
                                textFieldProps={{
                                  disabled: true,
                                  placeholder: "Total",
                                  value: toTwoDecimals(
                                    procedureCodeFormik.values?.[idx]?.total
                                  ),
                                  InputProps: dollarAdornment,
                                  sx: { width: "110px", flexShrink: 0 },
                                }}
                                label="Total"
                              />
                            </Flex>
                            {/* </Box> */}
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                </OutlineCard>
              </Flex>
            </Flex>
          ))}
        </Box>
      </CardView>
    </Box>
  );
};

export default ProcedureCodeGrid;
